import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getHoldCases,
  updateAllocatedCases,
} from "../../store/allocation/allocateSlice";
import CaseNotesModal from "../../components/allocation-dashboard/CaseNotesModal";
import { Helmet } from "react-helmet-async";
import {
  FaSearch,
  FaTimes,
  FaInbox,
  FaStickyNote,
  FaBolt,
} from "react-icons/fa";

const ACCENT = "#2fa9bf";

type PendingAction = {
  allocationId: number;
  action: "Unhold" | "Close";
  caseId: string;
} | null;

const formatCreation = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const HoldCases = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { holdCases, loading, error } = useAppSelector(
    (state) => state.caseAllocation,
  );

  const [open, setOpen] = useState(false);
  const [selectedAllocationId, setSelectedAllocationId] = useState<
    number | null
  >(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [passkey, setPasskey] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canAct =
    user?.role === "Super Admin" ||
    (Boolean(employee) &&
      employee.role !== "Designer" &&
      employee.role !== "Senior Designer" &&
      employee.role !== "CRM" &&
      employee.role !== "Operations");

  const filteredCases = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return holdCases ?? [];
    return (holdCases ?? []).filter(
      (item) =>
        item.case_id?.toLowerCase().includes(q) ||
        item.details?.toLowerCase().includes(q) ||
        item.note?.toLowerCase().includes(q),
    );
  }, [holdCases, searchTerm]);

  const handleSingleActionClick = (
    allocationId: number,
    action: "Unhold" | "Close",
    caseId: string,
  ) => {
    setPendingAction({ allocationId, action, caseId });
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
    setPendingAction(null);
    setPasskey("");
    setActionNote("");
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;

    const isAdmin = user?.role === "Super Admin";
    const finalPasskey = isAdmin ? user?.user_name : passkey;

    const payload = employee
      ? {
          empId: employee.emp_id,
          updateCases: [
            {
              allocationId: pendingAction.allocationId,
              action: pendingAction.action,
              action_note: actionNote,
            },
          ],
          isAdmin,
        }
      : {
          passkey: finalPasskey || "",
          updateCases: [
            {
              allocationId: pendingAction.allocationId,
              action: pendingAction.action,
              action_note: actionNote,
            },
          ],
          isAdmin,
        };

    setSubmitting(true);
    try {
      await dispatch(updateAllocatedCases(payload)).unwrap();
      await dispatch(getHoldCases());
      closeConfirm();
    } catch {
      // error surfaced via slice / toast elsewhere if configured
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(getHoldCases());
  }, [dispatch]);

  const confirmDisabled =
    submitting ||
    (!(user?.role === "Super Admin") && !employee && !passkey) ||
    !actionNote.trim();

  const colCount = canAct ? 5 : 4;

  return (
    <>
      <Helmet>
        <title>Hold Cases</title>
      </Helmet>

      <div className="flex flex-col gap-4 w-full min-w-0 text-sm relative">
        {/* Header */}
        <div
          className="rounded-2xl px-4 py-4 md:px-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
          style={{ backgroundColor: ACCENT }}
        >
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-white truncate">
              Hold Cases
            </h1>
            <p className="text-white/85 text-xs sm:text-sm mt-0.5">
              Review held allocations and unhold or close them
            </p>
          </div>

          <div className="relative w-full sm:w-[300px]">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] text-xs pointer-events-none" />
            <input
              type="text"
              placeholder="Search Case ID / details…"
              className="w-full rounded-xl bg-white border border-white text-[#1A1A18] placeholder:text-[#8A8A85] pl-9 pr-9 py-2.5 outline-none shadow-sm focus:ring-2 focus:ring-white/70 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A85] hover:text-[#4A4A48]"
              >
                <FaTimes className="text-xs" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-0.5">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#8A8A85]">
            {loading ? (
              <span>Loading hold cases…</span>
            ) : (
              <span>
                {filteredCases.length} case
                {filteredCases.length === 1 ? "" : "s"}
                {searchTerm.trim() ? " matched" : " on hold"}
              </span>
            )}
            {searchTerm.trim() ? (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="inline-flex items-center gap-1.5 text-[#2fa9bf] hover:text-[#2296aa] font-medium"
              >
                <FaTimes className="text-[10px]" />
                Clear search
              </button>
            ) : null}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-2xl border border-[#E8E8E4] shadow-sm">
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Case ID
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Details
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Held since
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Note
                </th>
                {canAct ? (
                  <th className="py-3 px-5 text-center text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>

            <tbody>
              {error ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-red-500"
                  >
                    Couldn’t load hold cases. Please try again.
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-[#8A8A85]"
                  >
                    <div className="inline-flex flex-col items-center gap-2">
                      <span
                        className="h-8 w-8 rounded-full border-2 border-[#E8E8E4] border-t-[#2fa9bf] animate-spin"
                        aria-hidden
                      />
                      Loading hold cases…
                    </div>
                  </td>
                </tr>
              ) : filteredCases.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-[#8A8A85]"
                  >
                    <div className="inline-flex flex-col items-center gap-2">
                      <FaInbox className="text-2xl text-[#DDDDD8]" />
                      <span>
                        {searchTerm.trim()
                          ? "No hold cases match your search"
                          : "No cases on hold"}
                      </span>
                      {searchTerm.trim() ? (
                        <button
                          type="button"
                          onClick={() => setSearchTerm("")}
                          className="text-[#2fa9bf] hover:underline"
                        >
                          Clear search
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCases.map((item) => (
                  <tr
                    key={item.allocation_id}
                    className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors"
                  >
                    <td className="py-3.5 px-5 align-middle">
                      <div className="flex flex-col gap-1 min-w-0">
                        <a
                          href={`/dashboard/admin/cases/${item.case_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#2fa9bf] hover:text-[#2296aa] hover:underline break-all"
                        >
                          {item.case_id}
                        </a>
                        {item.rush ? (
                          <span className="inline-flex w-fit items-center gap-1 rounded-md bg-orange-50 text-orange-700 text-[10px] font-medium px-1.5 py-0.5 ring-1 ring-orange-200">
                            <FaBolt className="text-[9px]" />
                            Rush
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className="py-3.5 px-5 align-middle break-words whitespace-pre-wrap max-w-[22rem] text-[#4A4A48]">
                      {item.details || "—"}
                    </td>

                    <td className="py-3.5 px-5 align-middle whitespace-nowrap text-[#4A4A48]">
                      {formatCreation(item.created_at)}
                    </td>

                    <td className="py-3.5 px-5 align-middle">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
                        {item.note ? (
                          <p className="text-[#4A4A48] break-words line-clamp-2 max-w-[16rem]">
                            {item.note}
                          </p>
                        ) : null}
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-lg bg-[#F0F7F8] text-[#2fa9bf] ring-1 ring-[#D4EBEF] hover:bg-[#E5F4F7] text-xs font-medium transition"
                          onClick={() => {
                            setOpen(true);
                            setSelectedAllocationId(item.allocation_id);
                          }}
                        >
                          <FaStickyNote className="text-[10px]" />
                          View
                        </button>
                      </div>
                    </td>

                    {canAct ? (
                      <td className="py-3.5 px-5 align-middle">
                        <div className="flex justify-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() =>
                              handleSingleActionClick(
                                item.allocation_id,
                                "Unhold",
                                item.case_id,
                              )
                            }
                            className="px-3 py-1.5 text-xs font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition"
                          >
                            Unhold
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleSingleActionClick(
                                item.allocation_id,
                                "Close",
                                item.case_id,
                              )
                            }
                            className="px-3 py-1.5 text-xs font-medium bg-red-500 hover:bg-red-600 text-white rounded-xl transition"
                          >
                            Close
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Confirm Modal */}
        {showConfirm && pendingAction ? (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4 border border-[#E8E8E4]">
              <div>
                <h3 className="text-base font-semibold text-[#4A4A48]">
                  Confirm {pendingAction.action.toLowerCase()}
                </h3>
                <p className="text-sm text-[#8A8A85] mt-1.5 leading-relaxed">
                  {pendingAction.action === "Unhold" ? "Unhold" : "Close"} case{" "}
                  <span className="font-medium text-[#2fa9bf]">
                    {pendingAction.caseId}
                  </span>
                  ?
                </p>
              </div>

              {!employee && user?.role !== "Super Admin" ? (
                <input
                  type="password"
                  placeholder="Enter passkey"
                  value={passkey}
                  onChange={(e) => setPasskey(e.target.value)}
                  className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20"
                />
              ) : null}

              <textarea
                placeholder="Enter action note (required)"
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 resize-none"
                rows={3}
              />

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeConfirm}
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={confirmDisabled}
                  className={`px-4 py-2 rounded-xl text-white text-xs font-medium transition disabled:opacity-50 ${
                    pendingAction.action === "Close"
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-orange-500 hover:bg-orange-600"
                  }`}
                >
                  {submitting ? "Saving…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <CaseNotesModal
          isOpen={open}
          onClose={() => setOpen(false)}
          allocationId={selectedAllocationId}
        />
      </div>
    </>
  );
};

export default HoldCases;
